import React from 'react';
import { FaChartBar, FaFilePdf, FaTimes, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

/* ─── helpers ─────────────────────────────────────────────────── */

const STATUS_CONFIG = {
  'eSigned':    { label: 'eSigned',    bg: '#dcfce7', color: '#166534', dot: '#22c55e' },
  'Signed':     { label: 'Signed',     bg: '#dcfce7', color: '#166534', dot: '#22c55e' },
  'Draft Sent': { label: 'Draft Sent', bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  'Pending':    { label: 'Pending',    bg: '#ffedd5', color: '#9a3412', dot: '#f97316' },
  'Rejected':   { label: 'Rejected',   bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
};


const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status || 'N/A', bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      background: cfg.bg, color: cfg.color,
      borderRadius: '999px', padding: '3px 10px',
      fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

/* ─── Mobile card for one record ──────────────────────────────── */
const MobileCard = ({ item, index, handleExportPDF }) => {
  const rows = [
    { label: 'Tax Year',          value: item.tax_year,              bold: true },
    { label: 'File Name',         value: item.file_name,             truncate: true },
    { label: 'Consent Time',      value: item.consent_timestamp,     muted: true },
    { label: 'Typed Name',        value: item.typed_name },
    { label: 'IP Address',        value: item.public_ip,             mono: true },
    { label: 'Location',          value: item.resolved_location || item.client_location },
    { label: 'Platform',          value: item.device_platform,       muted: true },
    { label: 'Browser',           value: item.browser_engine,        muted: true, truncate: true },
    { label: 'Created At',        value: item.createdAt,             muted: true },
  ];

  return (
    <div className="esign-mobile-card">
      {/* Card top: status + agreed */}
      <div className="esign-mobile-card-top">
        <StatusBadge status={item.status} />
        <span className={item.agreed_to_file ? 'esign-agreed-yes' : 'esign-agreed-no'}>
          {item.agreed_to_file
            ? <><FaCheckCircle size={12} /> Agreed</>
            : <><FaTimesCircle size={12} /> Not Agreed</>}
        </span>
      </div>

      {/* Detail rows */}
      <div className="esign-mobile-rows">
        {rows.map(({ label, value, bold, muted, mono, truncate }) =>
          value ? (
            <div key={label} className="esign-mobile-row">
              <span className="esign-mobile-label">{label}</span>
              <span
                className={`esign-mobile-value${truncate ? ' esign-mobile-truncate' : ''}`}
                style={{
                  fontWeight: bold ? 700 : 500,
                  color: muted ? '#64748b' : '#1e293b',
                  fontFamily: mono ? 'monospace' : 'inherit',
                }}
                title={truncate ? value : undefined}
              >
                {value}
              </span>
            </div>
          ) : null
        )}

        {/* Link row */}
        {item.shared_link && (
          <div className="esign-mobile-row">
            <span className="esign-mobile-label">Document</span>
            <a
              href={item.shared_link}
              target="_blank"
              rel="noopener noreferrer"
              className="esign-mobile-link"
            >
              Open ↗
            </a>
          </div>
        )}
      </div>

      {/* Export button */}
      <button className="esign-export-btn esign-export-full" onClick={() => handleExportPDF(item)}>
        <FaFilePdf size={12} /> Export Audit Report
      </button>
    </div>
  );
};

/* ─── component ───────────────────────────────────────────────── */

const ESignDetailsModal = ({ details, onClose }) => {
  if (!details) return null;

  const detailsArray = Array.isArray(details) ? details : [details];
  const customerName = detailsArray.length > 0
    ? (detailsArray[0].customerName || detailsArray[0].name || 'N/A')
    : 'N/A';

  const handleExportPDF = (item) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const htmlContent = `<!DOCTYPE html>
<html>
  <head>
    <title>${item.tax_year || ''} E-Sign Audit Log - ${customerName}</title>
    <style>
      body { font-family: monospace; white-space: pre; padding: 40px; font-size: 14px; line-height: 1.5; color: #000; }
      @media print { body { padding: 0; } }
    </style>
  </head>
  <body>====================================================================
ELECTRONIC CONSENT AUDIT LOG - ORIENTAL BUSINESS SOLUTIONS INC.
====================================================================
Document Type:       Form T183 (Information Return for Electronic Filing)
Tax Module Track:    Tax Workflow
Target Tax Year:     ${item.tax_year || 'N/A'}
OneDrive File Ref:   ${item.file_name || 'N/A'}

Client Name:         ${customerName}
Client Email:        ${item.email || 'N/A'}
Secure Portal Token: ${item.portal_token || 'N/A'}

Execution Metadata:
------------------
CRA EFILE Date:      ${item.consent_date || (item.consent_timestamp ? item.consent_timestamp.split(' ')[0] : 'N/A')}
CRA EFILE Time:      ${item.consent_time || (item.consent_timestamp ? item.consent_timestamp.split(' ')[1] + ' ' + item.consent_timestamp.split(' ')[2] : 'N/A')}
Public IP Address:   ${item.public_ip || 'N/A'}
Resolved Location:   ${item.resolved_location || item.client_location || 'N/A'}
Device Platform:     ${item.device_platform || 'N/A'}
Browser Engine:      ${item.browser_engine || 'N/A'}

Legal Declaration (Form T183 Part F Compliance):
-----------------------------------------------
By checking the authorization box and clicking "Confirm", the user 
explicitly declares that the information given in their electronic return 
is correct and complete, and that they select EFILE transmission.

[Electronic Confirmation - SECURELY LOGGED]
Authorized By:       ${item.typed_name || customerName}
Consent Status:      VERIFIED / ${item.agreed_to_file ? 'Agreed to File' : 'Did Not Agree'}
====================================================================</body>
  <script>
    window.onload = function() {
      window.print();
      setTimeout(() => { window.close(); }, 100);
    }
  </script>
</html>`;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <>
      <style>{`
        @keyframes esign-modal-in {
          from { opacity: 0; transform: scale(0.96) translateY(12px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);    }
        }
        .esign-modal-dialog {
          animation: esign-modal-in 0.22s cubic-bezier(0.16,1,0.3,1);
        }

        /* ── header ── */
        .esign-modal-header {
          background: linear-gradient(135deg, #1B3A5C 0%, #1D4ED8 100%);
          padding: 1.1rem 1.5rem;
          border-radius: 1rem 1rem 0 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .esign-close-btn {
          width: 32px; height: 32px;
          border-radius: 50%;
          border: 1.5px solid rgba(255,255,255,0.35);
          background: rgba(255,255,255,0.1);
          color: #fff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: background 0.18s ease, border-color 0.18s ease;
          flex-shrink: 0; padding: 0;
        }
        .esign-close-btn:hover {
          background: rgba(255,255,255,0.22);
          border-color: rgba(255,255,255,0.6);
        }

        /* ── export button ── */
        .esign-export-btn {
          display: inline-flex; align-items: center; gap: 5px;
          background: transparent;
          border: 1.5px solid #bfdbfe;
          color: #1d4ed8;
          border-radius: 999px;
          padding: 4px 12px;
          font-size: 0.78rem;
          font-weight: 600;
          white-space: nowrap;
          cursor: pointer;
          transition: all 0.18s ease;
        }
        .esign-export-btn:hover {
          background: #1d4ed8;
          border-color: #1d4ed8;
          color: #fff;
          box-shadow: 0 4px 12px rgba(29,78,216,0.28);
          transform: translateY(-1px);
        }

        /* ── agreed chips ── */
        .esign-agreed-yes {
          display: inline-flex; align-items: center; gap: 4px;
          color: #166534; font-weight: 600; font-size: 0.8rem;
        }
        .esign-agreed-no {
          display: inline-flex; align-items: center; gap: 4px;
          color: #991b1b; font-weight: 600; font-size: 0.8rem;
        }

        /* ── DESKTOP TABLE ── */
        .esign-table {
          border-collapse: separate;
          border-spacing: 0;
          font-size: 0.83rem;
          width: 100%;
        }
        .esign-table thead th {
          background: #f8fafc;
          color: #475569;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 10px 14px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
          position: sticky; top: 0; z-index: 1;
        }
        .esign-table thead th:first-child { border-radius: 8px 0 0 0; }
        .esign-table thead th:last-child  { border-radius: 0 8px 0 0; }
        .esign-table tbody tr { transition: background 0.12s ease; }
        .esign-table tbody tr:hover td { background: #f1f5f9; }
        .esign-table tbody td {
          padding: 11px 14px;
          border-bottom: 1px solid #f1f5f9;
          color: #374151;
          vertical-align: middle;
          white-space: nowrap;
        }
        .esign-table tbody tr:last-child td { border-bottom: none; }
        .esign-cell-truncate {
          max-width: 160px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          display: block;
        }

        /* ── MOBILE CARDS ── */
        .esign-mobile-list {
          display: none;
          flex-direction: column;
          gap: 12px;
          padding: 1rem;
        }
        .esign-mobile-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(15,23,42,0.06);
        }
        .esign-mobile-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 14px 10px;
          border-bottom: 1px solid #f1f5f9;
          background: #fafbff;
        }
        .esign-mobile-rows {
          padding: 4px 0;
        }
        .esign-mobile-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          padding: 9px 14px;
          border-bottom: 1px solid #f8fafc;
        }
        .esign-mobile-row:last-child { border-bottom: none; }
        .esign-mobile-label {
          font-size: 0.7rem;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          white-space: nowrap;
          min-width: 90px;
          padding-top: 1px;
        }
        .esign-mobile-value {
          font-size: 0.82rem;
          text-align: right;
          word-break: break-word;
        }
        .esign-mobile-truncate {
          max-width: 170px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }
        .esign-mobile-link {
          font-size: 0.82rem;
          font-weight: 600;
          color: #1d4ed8;
          text-decoration: none;
        }
        .esign-export-full {
          width: 100%;
          border-radius: 0 0 14px 14px !important;
          border: none !important;
          border-top: 1px solid #e2e8f0 !important;
          padding: 11px 16px !important;
          justify-content: center;
          font-size: 0.82rem !important;
          background: #f8fafc !important;
          color: #1d4ed8 !important;
        }
        .esign-export-full:hover {
          background: #1d4ed8 !important;
          color: #fff !important;
          transform: none !important;
        }

        /* ── show/hide at breakpoint ── */
        @media (max-width: 575.98px) {
          .esign-desktop-table { display: none !important; }
          .esign-mobile-list   { display: flex !important; }
        }
        @media (min-width: 576px) {
          .esign-mobile-list   { display: none !important; }
        }

        /* ── footer ── */
        .esign-modal-footer {
          border-top: 1px solid #e5e7eb;
          padding: 0.85rem 1.5rem;
          display: flex;
          justify-content: flex-end;
          background: #f8fafc;
          border-radius: 0 0 1rem 1rem;
        }
        .esign-close-footer-btn {
          display: inline-flex; align-items: center; gap: 6px;
          background: #1B3A5C; color: #fff; border: none;
          border-radius: 999px; padding: 9px 24px;
          font-size: 0.88rem; font-weight: 600; cursor: pointer;
          transition: background 0.18s ease, box-shadow 0.18s ease;
        }
        .esign-close-footer-btn:hover {
          background: #1D4ED8;
          box-shadow: 0 6px 18px rgba(29,78,216,0.3);
        }

        /* ── empty state ── */
        .esign-empty {
          padding: 3rem 2rem; text-align: center; color: #94a3b8;
        }
        .esign-empty svg { margin-bottom: 0.75rem; opacity: 0.4; }
      `}</style>

      {/* Backdrop */}
      <div
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15,23,42,0.55)',
          backdropFilter: 'blur(3px)',
          zIndex: 1540,
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal wrapper */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ zIndex: 1550 }}
        role="dialog"
        aria-modal="true"
        aria-label={`eSign Details for ${customerName}`}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl esign-modal-dialog"
          style={{ maxWidth: 'min(92vw, 1200px)', margin: '1.5rem auto' }}
        >
          <div className="modal-content border-0" style={{ borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 25px 60px rgba(15,23,42,0.22)' }}>

            {/* ── Header ── */}
            <div className="esign-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', minWidth: 0 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '9px',
                  background: 'rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <FaChartBar size={16} color="#fff" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 1 }}>
                    eSign Audit Log
                  </div>
                  <h5 style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60vw' }}>
                    {customerName}
                  </h5>
                </div>
              </div>
              <button className="esign-close-btn" onClick={onClose} aria-label="Close modal">
                <FaTimes size={13} />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="modal-body p-0" style={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 'calc(85vh - 130px)' }}>
              {detailsArray.length === 0 ? (
                <div className="esign-empty">
                  <FaChartBar size={36} />
                  <p style={{ fontWeight: 600, color: '#64748b', marginTop: '0.5rem' }}>No e-sign details available.</p>
                </div>
              ) : (
                <>
                  {/* ════ DESKTOP TABLE (≥576px) ════ */}
                  <div className="esign-desktop-table" style={{ overflowX: 'auto' }}>
                    <table className="esign-table">
                      <thead>
                        <tr>
                          <th>Action</th>
                          <th>Tax Year</th>
                          <th>File Name</th>
                          <th>Status</th>
                          <th>Agreed</th>
                          <th>Consent Timestamp</th>
                          <th>Typed Name</th>
                          <th>IP Address</th>
                          <th>Location</th>
                          <th>Platform</th>
                          <th>Browser</th>
                          <th>Link</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsArray.map((item, index) => (
                          <tr key={item.id || index}>
                            <td>
                              <button className="esign-export-btn" onClick={() => handleExportPDF(item)} title="Export audit report as PDF">
                                <FaFilePdf size={11} /> Export
                              </button>
                            </td>
                            <td style={{ fontWeight: 700, color: '#1e3a5f' }}>{item.tax_year || 'N/A'}</td>
                            <td style={{ maxWidth: 180 }} title={item.file_name}>
                              <span className="esign-cell-truncate">{item.file_name || 'N/A'}</span>
                            </td>
                            <td><StatusBadge status={item.status} /></td>
                            <td>
                              {item.agreed_to_file
                                ? <span className="esign-agreed-yes"><FaCheckCircle size={13} /> Yes</span>
                                : <span className="esign-agreed-no"><FaTimesCircle size={13} /> No</span>}
                            </td>
                            <td style={{ maxWidth: 170 }} title={item.consent_timestamp}>
                              <span className="esign-cell-truncate" style={{ color: '#475569', fontSize: '0.8rem' }}>{item.consent_timestamp || 'N/A'}</span>
                            </td>
                            <td style={{ fontWeight: 600 }}>{item.typed_name || 'N/A'}</td>
                            <td style={{ color: '#64748b', fontSize: '0.8rem', fontFamily: 'monospace' }}>{item.public_ip || 'N/A'}</td>
                            <td style={{ maxWidth: 140 }} title={item.resolved_location || item.client_location}>
                              <span className="esign-cell-truncate" style={{ color: '#475569' }}>{item.resolved_location || item.client_location || 'N/A'}</span>
                            </td>
                            <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{item.device_platform || 'N/A'}</td>
                            <td style={{ maxWidth: 140 }} title={item.browser_engine}>
                              <span className="esign-cell-truncate" style={{ color: '#64748b', fontSize: '0.78rem' }}>{item.browser_engine || 'N/A'}</span>
                            </td>
                            <td>
                              {item.shared_link
                                ? <a href={item.shared_link} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 600, fontSize: '0.8rem', textDecoration: 'none' }}>Open ↗</a>
                                : <span style={{ color: '#cbd5e1' }}>—</span>}
                            </td>
                            <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{item.createdAt || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* ════ MOBILE CARDS (<576px) ════ */}
                  <div className="esign-mobile-list">
                    {detailsArray.map((item, index) => (
                      <MobileCard
                        key={item.id || index}
                        item={item}
                        index={index}
                        handleExportPDF={handleExportPDF}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* ── Footer ── */}
            <div className="esign-modal-footer">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                  {detailsArray.length} record{detailsArray.length !== 1 ? 's' : ''}
                </span>
                <button className="esign-close-footer-btn" onClick={onClose}>
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default ESignDetailsModal;
